/*

? The chat list's rows (rebuilt 18 Oct 2026 to the app's, bbm-android ChatScreen): one list from
? chat/fetch-user-chats, where pending broadcast invites already sit as rows
? (lib/chat/userChatListCore.ts), filtered on the device by the chips (All, Unread, Requirements,
? Broadcasts) over the pages loaded. The separate Requests list (broadcast/fetch-received-broadcasts)
? is gone, as in the app.

  - Page 1 on first open and for every search (any length, from the first character); the next page
    when the bottom comes into view. A failed page 1 shows the error view with Try again; a failed
    later page keeps the rows and offers Try again at the bottom.
  - Empty: "No chats yet" when there are none at all, "No chats found" when a search or chip matches
    nothing (the app's wording).

*/

/* Common Imports */

import { styled } from "@mui/system";
import React from "react";

/* Redux & Type Imports */

import { NextRouter } from "next/router";
import StateInterface from "../../../../redux-magic/state-interface";
import {
	fetchUserChatsThunk,
	setChatListAllCountThunk,
	setChatListFilterPageNumberThunk,
	setChatListInitialLoadDoneThunk,
	setChatListTriggerNewSearchThunk,
} from "../../../../redux-magic/store";
import { ChatInterface } from "../../../../redux-magic/sub-interfaces/sub-interfaces";

/* Component Imports */

import { CircularProgress, SvgIcon, Typography } from "@mui/material";
import { useInView } from "react-intersection-observer";
import NoChat from "../../../../public/images/broadcast/no_chat.svg";
import BeegruButton from "../../../common-components/buttons/BeegruButton";
import ChatListCard from "./ChatListCard";

/* Styled Components */

const RootDiv = styled("div")(({ theme }) => ({
	background: theme.palette.background.paper,
	borderRadius: "0rem",
	height: "100%",
	width: "100%",
	padding: "0rem 0rem 0rem 0rem",
}));

const ChatListDiv = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "flex-start",
	alignItems: "flex-start",
	width: "100%",
	height: "100%",
	gap: "1.5rem",
	overflow: "auto",
	overflowX: "hidden",
	padding: "1rem 1.25rem 21rem 1.25rem",
	position: "relative",
	overscrollBehavior: "contain",
	isolation: "isolate",
	"&::-webkit-scrollbar": {
		width: "6px",
		background: "transparent",
	},
	"&::-webkit-scrollbar-thumb": {
		background: "rgba(0, 0, 0, 0.2)",
		borderRadius: "3px",
		"&:hover": {
			background: "rgba(0, 0, 0, 0.3)",
		},
	},
	"&": {
		scrollbarWidth: "thin",
		scrollbarColor: "rgba(0, 0, 0, 0.2) transparent",
	},
	"&:not(:hover)::-webkit-scrollbar-thumb": {
		background: "transparent",
	},
}));

const LoadMoreDiv = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "center",
	alignItems: "center",
	width: "100%",
	height: "7.5rem",
	pointerEvents: "none",
	cursor: "none",
	background: "transparent",
}));

const ColumnContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "space-between",
	alignItems: "center",
}));

/* The chips' keys (chat_states.chat_list_tab). An older "chats" means All. */
export type ChatListFilter = "all" | "unread" | "requirements" | "broadcasts";

export const chatListFilterOf = (tab: string): ChatListFilter =>
	tab === "unread" || tab === "requirements" || tab === "broadcasts" ? tab : "all";

const ChatList = ({
	session,
	dispatch,
	router,
	profile_context,
	chat_list,
	search_term,
	clearAutoComplete,
	filter,
	typing_chat_ids,
}: {
	session: StateInterface["redux_session"];
	dispatch: Function;
	router: NextRouter;
	profile_context: any;
	chat_list: StateInterface["chat_states"]["chat_list"];
	search_term: string;
	clearAutoComplete: boolean;
	filter: ChatListFilter;
	typing_chat_ids?: Array<string>;
}) => {
	/* Translations */

	const ar: boolean = router.locale === "ar-AE";
	const kn: boolean = router.locale === "kn-IN";
	const localLang = {
		no_chats_yet: ar ? "لا توجد محادثات بعد" : kn ? "ಇನ್ನೂ ಯಾವುದೇ ಚಾಟ್‌ಗಳಿಲ್ಲ" : "No chats yet",
		no_chats_yet_note: ar
			? "ستظهر هنا المحادثات من إعلاناتك وبثّك ومتطلباتك."
			: kn
				? "ನಿಮ್ಮ ಪಟ್ಟಿಗಳು, ಪ್ರಸಾರಗಳು ಮತ್ತು ಅವಶ್ಯಕತೆಗಳಿಂದ ಬರುವ ಸಂಭಾಷಣೆಗಳು ಇಲ್ಲಿ ಕಾಣಿಸುತ್ತವೆ."
				: "Conversations from your listings, broadcasts and requirements show up here.",
		no_chats_found: ar ? "لم يتم العثور على أي محادثات" : kn ? "ಯಾವುದೇ ಚಾಟ್‌ಗಳು ಕಂಡುಬಂದಿಲ್ಲ" : "No chats found",
		no_chats_found_note: ar ? "جرّب اسمًا أو رسالة مختلفة." : kn ? "ಬೇರೆ ಹೆಸರು ಅಥವಾ ಸಂದೇಶವನ್ನು ಪ್ರಯತ್ನಿಸಿ." : "Try a different name or message.",
		couldnt_load_chats: ar ? "تعذر تحميل محادثاتك" : kn ? "ನಿಮ್ಮ ಚಾಟ್‌ಗಳನ್ನು ಲೋಡ್ ಮಾಡಲು ಸಾಧ್ಯವಾಗಲಿಲ್ಲ" : "Couldn't load your chats",
		couldnt_load_more: ar ? "تعذر تحميل المزيد" : kn ? "ಇನ್ನಷ್ಟು ಲೋಡ್ ಮಾಡಲು ಸಾಧ್ಯವಾಗಲಿಲ್ಲ" : "Couldn't load more",
		try_again: ar ? "حاول مرة أخرى" : kn ? "ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ" : "Try again",
	};

	type ListLoadState = "idle" | "loading" | "error";

	// "loading" until the list has been asked for once, so "No chats yet" never flashes first.
	const [chatsLoadState, setChatsLoadState] = React.useState<ListLoadState>(
		chat_list.initial_load_done ? "idle" : "loading",
	);
	const [loadMoreFailed, setLoadMoreFailed] = React.useState<boolean>(false);

	// Page-1 loads still in flight: no later page is asked for while one is.
	const chatsPageOnePending = React.useRef<number>(0);
	const loadingMoreRef = React.useRef<boolean>(false);

	const listRequestBase = () => ({
		is_business_profile: profile_context.is_business_profile,
		user_id: session ? (session.user ? session.user.dbUser._id : "") : "",
		business_profile_id: profile_context.business_profile_id,
		sessionId: session ? (session.session_id ? session.session_id : "") : "",
		/* The chip is applied by the server before paging (3 Nov 2026, as the apps do), so Unread, Requirements and
		   Broadcasts show every match, not only those on the pages already loaded. */
		...(filter !== "all" ? { filter } : {}),
	});

	const loadChatsPageOne = async () => {
		dispatch(setChatListTriggerNewSearchThunk(true));
		dispatch(setChatListInitialLoadDoneThunk(true));
		chatsPageOnePending.current++;
		setChatsLoadState("loading");
		const term: string = (search_term || "").trim();
		try {
			const outcome = await dispatch(fetchUserChatsThunk({ ...listRequestBase(), page: 1, search_term: term }));
			if (!outcome || outcome.stale) return;
			if (outcome.ok) {
				dispatch(setChatListFilterPageNumberThunk(1));
				setLoadMoreFailed(false);
			}
			setChatsLoadState(outcome.ok ? "idle" : "error");
		} finally {
			chatsPageOnePending.current--;
		}
	};

	const hasMore: boolean = chat_list.chats_count > chat_list.chats.length;

	const loadMore = async () => {
		if (loadingMoreRef.current || !session || chatsPageOnePending.current > 0) return;
		loadingMoreRef.current = true;
		try {
			const nextPage: number = (chat_list.filter_page_number || 1) + 1;
			dispatch(setChatListTriggerNewSearchThunk(false));
			const outcome = await dispatch(
				fetchUserChatsThunk({ ...listRequestBase(), page: nextPage, search_term: (search_term || "").trim() }),
			);
			if (!outcome || outcome.stale) return;
			if (outcome.ok) dispatch(setChatListFilterPageNumberThunk(nextPage));
			setLoadMoreFailed(!outcome.ok);
		} finally {
			loadingMoreRef.current = false;
		}
	};

	/* Page 1 on first open, after a search is cleared, and for every search term (any length). */

	const lastSearch = React.useRef<string>((search_term || "").trim());
	React.useEffect(() => {
		const term: string = (search_term || "").trim();
		const searchChanged: boolean = term !== lastSearch.current;
		lastSearch.current = term;
		if ((!chat_list.initial_load_done && session) || clearAutoComplete || searchChanged) {
			loadChatsPageOne();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [session, clearAutoComplete, search_term]);

	/* A chip change reads page 1 again, narrowed by the server. */
	const lastFilter = React.useRef<ChatListFilter>(filter);
	React.useEffect(() => {
		if (lastFilter.current === filter) return;
		lastFilter.current = filter;
		if (session) loadChatsPageOne();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filter]);

	/* The total with no search term decides whether search is offered (more than 5, the app's rule). */

	React.useEffect(() => {
		// Only an unfiltered list says how many chats there really are.
		if (chatsLoadState === "idle" && (search_term || "").trim() === "" && filter === "all") {
			dispatch(setChatListAllCountThunk(chat_list.chats_count));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [chatsLoadState, chat_list.chats_count, search_term, filter]);


	const viewerUnreadOf = (chat: any): number =>
		Number(
			(chat.unread_messages_count || []).find(
				(count: any) =>
					(count.is_business_profile &&
						count.business_profile_id?.toString() === profile_context.business_profile_id?.toString()) ||
					(!count.is_business_profile && count.user_id?.toString() === profile_context.user_id?.toString()),
			)?.unread_messages_count || 0,
		);

	/* The server already narrowed the rows to the chip (listRequestBase). */
	const rows: Array<ChatInterface> = chat_list.chats || [];

	/* The next page when the bottom of the rows on show comes into view (infinite scroll) */

	const { ref, inView } = useInView({ threshold: 0 });

	React.useEffect(() => {
		if (inView && hasMore && !loadMoreFailed && session) {
			loadMore();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [inView, filter, hasMore, loadMoreFailed, chat_list.chats.length]);

	/* A failed first page: the error view, with Try again */

	if (chatsLoadState === "error") {
		return (
			<ColumnContainer sx={{ justifyContent: "flex-start", width: "100%", height: "100%", padding: "4rem 1rem", gap: "1rem" }}>
				<Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: "0.875rem", fontWeight: 500, textAlign: "center" }}>
					{localLang.couldnt_load_chats}
				</Typography>
				<BeegruButton variant="outlined" flavor="primary" onClick={() => loadChatsPageOne()}>
					{localLang.try_again}
				</BeegruButton>
			</ColumnContainer>
		);
	}

	/* The first page on its way, with nothing to show yet */

	if (chatsLoadState === "loading" && chat_list.chats.length === 0) {
		return (
			<ColumnContainer sx={{ justifyContent: "flex-start", width: "100%", height: "100%", padding: "4rem 1rem" }}>
				<CircularProgress />
			</ColumnContainer>
		);
	}

	/* Nothing to show: "No chats yet" with no chats at all, "No chats found" for a search or chip. */

	if (rows.length === 0 && !hasMore) {
		const noneAtAll: boolean = (search_term || "").trim() === "" && filter === "all";
		/* A chip with no search has its own words (3 Nov 2026, the app's ChatScreen). */
		const chipEmpty: [string, string] | null =
			(search_term || "").trim() !== ""
				? null
				: filter === "unread"
					? [ar ? "لا توجد محادثات غير مقروءة" : kn ? "ಓದದ ಚಾಟ್‌ಗಳಿಲ್ಲ" : "No unread chats", ar ? "لقد اطلعت على كل شيء." : kn ? "ನೀವು ಎಲ್ಲವನ್ನೂ ನೋಡಿದ್ದೀರಿ." : "You're all caught up."]
					: filter === "requirements"
						? [
								ar ? "لا توجد محادثات متطلبات" : kn ? "ಅಗತ್ಯದ ಚಾಟ್‌ಗಳಿಲ್ಲ" : "No requirement chats",
								ar
									? "تظهر هنا المحادثات حول المتطلبات التي تنشرها أو تطالب بها."
									: kn
										? "ನೀವು ಪೋಸ್ಟ್ ಮಾಡುವ ಅಥವಾ ಕ್ಲೈಮ್ ಮಾಡುವ ಅಗತ್ಯಗಳ ಚಾಟ್‌ಗಳು ಇಲ್ಲಿ ಕಾಣಿಸುತ್ತವೆ."
										: "Chats about requirements you post or claim show up here.",
							]
						: filter === "broadcasts"
							? [
									ar ? "لا توجد محادثات بث" : kn ? "ಪ್ರಸಾರದ ಚಾಟ್‌ಗಳಿಲ್ಲ" : "No broadcast chats",
									ar
										? "تظهر هنا المحادثات حول البث الذي ترسله أو تتلقاه."
										: kn
											? "ನೀವು ಕಳುಹಿಸುವ ಅಥವಾ ಸ್ವೀಕರಿಸುವ ಪ್ರಸಾರಗಳ ಚಾಟ್‌ಗಳು ಇಲ್ಲಿ ಕಾಣಿಸುತ್ತವೆ."
											: "Chats about broadcasts you send or receive show up here.",
								]
							: null;
		return (
			<ColumnContainer sx={{ justifyContent: "flex-start", width: "100%", height: "100%", padding: "3rem 1.5rem", gap: "0.75rem" }}>
				<SvgIcon
					component={NoChat}
					inheritViewBox={false}
					viewBox="0 0 120 121"
					sx={{ width: "7rem", height: "7rem", fill: "none" }}
				/>
				<Typography variant="h6" color="text.primary" sx={{ fontSize: "1rem", fontWeight: 600, textAlign: "center" }}>
					{noneAtAll ? localLang.no_chats_yet : chipEmpty ? chipEmpty[0] : localLang.no_chats_found}
				</Typography>
				<Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: "0.8125rem", textAlign: "center" }}>
					{noneAtAll ? localLang.no_chats_yet_note : chipEmpty ? chipEmpty[1] : localLang.no_chats_found_note}
				</Typography>
			</ColumnContainer>
		);
	}

	const typing: Set<string> = new Set((typing_chat_ids || []).map((id) => String(id)));

	return (
		<RootDiv>
			<ChatListDiv>
				{rows.map((chat: any, index: number) => (
					<ChatListCard
						property_id={chat.broadcast?.property_id}
						// The row's own id, so a list that shifts never reuses another row's state (chat audit I12).
						key={String(chat?._id || "") || index}
						session={session}
						dispatch={dispatch}
						router={router}
						profile_context={profile_context}
						chat_id={chat._id}
						chat_type={chat.chat_type}
						chat_title={chat.chat_title}
						chat_description={chat.chat_description}
						chat_dp={chat.chat_dp}
						chat_notification_settings={chat.notification_settings?.[0]}
						chat_participants={chat.participants}
						chat_last_message={chat.last_message}
						chat_created_at={chat.created_at as string}
						chat_is_broadcast={chat.is_broadcast}
						chat_unread_messages_count={{
							is_business_profile: profile_context.is_business_profile,
							user_id: profile_context.user_id,
							business_profile_id: profile_context.business_profile_id,
							unread_messages_count: viewerUnreadOf(chat),
						}}
						isBroadcast={chat.chat_source_collection === "broadcast"}
						broadcast_id={chat.broadcast?._id}
						invite_status={chat.chat_source_collection === "broadcast" ? chat.recipient?.invite_status : undefined}
						is_request={chat.is_request === true}
						server_preview={chat.last_message_preview?.text}
						preview_kind={chat.last_message_preview?.kind}
						is_unavailable={chat.is_unavailable === true}
						is_typing={typing.has(String(chat._id)) || (chat.group_chat_ids || []).some((id: any) => typing.has(String(id)))}
						highlight={search_term}
					/>
				))}

				{/* The next page: a spinner while it loads, Try again if it failed */}

				{hasMore && !loadMoreFailed && (
					<LoadMoreDiv ref={ref}>
						<CircularProgress />
					</LoadMoreDiv>
				)}
				{hasMore && loadMoreFailed && (
					<ColumnContainer sx={{ width: "100%", gap: "0.5rem", padding: "1rem 0rem" }}>
						<Typography variant="subtitle2" color="text.secondary" sx={{ fontSize: "0.8125rem" }}>
							{localLang.couldnt_load_more}
						</Typography>
						<BeegruButton variant="text" flavor="primary" onClick={() => setLoadMoreFailed(false)}>
							{localLang.try_again}
						</BeegruButton>
					</ColumnContainer>
				)}
			</ChatListDiv>
		</RootDiv>
	);
};

export default ChatList;
